<template>
  <Teleport to="body">
    <div v-if="showModal" :class="$style.modalOverlay" @click="closeModal">
      <div :class="$style.modalContent" @click.stop>
        <div :class="$style.modalHeader">
          <h3 :class="$style.modalTitle">Game Details</h3>
          <button :class="$style.closeButton" @click="closeModal">
            <img :src="closeIcon" alt="Close" :class="$style.closeIcon" />
          </button>
        </div>

        <div :class="$style.tabsContainer">
          <button
            v-for="tab in visibleTabs"
            :key="tab.id"
            :class="[$style.tab, { [$style.activeTab]: activeTab === tab.id }]"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <div :class="$style.modalBody">
          <div v-if="loading" :class="$style.loading">Loading game details...</div>
          <div v-else-if="error" :class="$style.error">{{ error }}</div>

          <div v-else-if="gameDetails" :class="$style.tabContent">
            <div v-if="activeTab === 'replay'" :class="[$style.tabPanel, $style.replayLauncher]">
              <h4 :class="$style.sectionTitle">{{ $t("replay.tabTitle") }}</h4>
              <p :class="$style.replayLauncherText">{{ $t("replay.tabText") }}</p>
              <button type="button" :class="$style.downloadBtn" @click="openReplay">{{ $t("replay.openWindow") }}</button>
            </div>
            <div v-if="activeTab === 'overview'" :class="$style.tabPanel">
              <div :class="$style.section">
                <h4 :class="$style.sectionTitle">Game Information</h4>
                <div :class="$style.detailsGrid">
                  <div v-for="field in overviewFields" :key="field.key" :class="$style.detailItem">
                    <span :class="$style.detailLabel">{{ field.label }}:</span>
                    <span :class="$style.detailValue">{{ formatOverviewValue(field) }}</span>
                  </div>
                </div>
              </div>

              <div :class="$style.section">
                <h4 :class="$style.sectionTitle">Game Configuration</h4>
                <div :class="$style.detailsGrid">
                  <div v-for="field in configFields" :key="field.key" :class="$style.detailItem">
                    <span :class="$style.detailLabel">{{ field.label }}:</span>
                    <span :class="$style.detailValue">{{ formatConfigValue(field) }}</span>
                  </div>
                </div>
              </div>

              <div v-if="gameDetails.texts && gameDetails.texts.length > 0" :class="$style.section">
                <h4 :class="$style.sectionTitle">Game Texts</h4>
                <div :class="$style.detailsGrid">
                  <div v-for="field in textSummaryFields" :key="field.key" :class="$style.detailItem">
                    <span :class="$style.detailLabel">{{ field.label }}:</span>
                    <span :class="$style.detailValue">{{ field.value }}</span>
                  </div>
                </div>
                <div :class="$style.textsList">
                  <div
                    v-for="(text, index) in gameDetails.texts"
                    :key="text.id"
                    :class="[$style.textItem, { [$style.realText]: text.isReal }]"
                  >
                    <div :class="$style.textHeader">
                      <span :class="$style.textIndex">{{ text.label || textLabel(index) }}</span>
                      <span v-if="text.isReal" :class="$style.realBadge">Most Valid</span>
                    </div>
                    <div :class="$style.textContent">
                      {{ text.content }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeTab === 'players'" :class="$style.tabPanel">
              <div :class="$style.section">
                <h4 :class="$style.sectionTitle">Players List</h4>
                <div :class="$style.playersTable">
                  <table :class="$style.table">
                    <thead>
                      <tr>
                        <th>Player ID</th>
                        <th>Username</th>
                        <th>Role</th>
                        <th>Group</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="player in playersWithoutHost" :key="player.id">
                        <td>{{ player.id }}</td>
                        <td>{{ player.username }}</td>
                        <td>
                          <span :class="[$style.roleBadge, $style[player.role]]">
                            {{ player.role || "player" }}
                          </span>
                        </td>
                        <td>{{ player.groupName || "N/A" }}</td>
                        <td>{{ player.score !== null && player.score !== undefined ? player.score : "N/A" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="gameDetails.preliminaryVotes && gameDetails.preliminaryVotes.length > 0" :class="$style.section">
                <h4 :class="$style.sectionTitle">Preliminary Votes</h4>
                <div :class="$style.votesList">
                  <div v-for="vote in gameDetails.preliminaryVotes" :key="vote.playerId" :class="$style.voteItem">
                    <div :class="$style.voteHeader">
                      <strong>{{ vote.playerUsername }}</strong>
                    </div>
                    <div :class="$style.voteDetails">
                      <div>Vote 1: {{ vote.vote1 || "N/A" }}</div>
                      <div>Vote 2: {{ vote.vote2 || "N/A" }}</div>
                      <div v-if="vote.voteReason" :class="$style.voteReason">
                        <strong>Reason:</strong> {{ vote.voteReason }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="gameDetails.finalVotes && gameDetails.finalVotes.length > 0" :class="$style.section">
                <h4 :class="$style.sectionTitle">Final Votes</h4>
                <div :class="$style.votesList">
                  <div v-for="vote in gameDetails.finalVotes" :key="vote.playerId" :class="$style.voteItem">
                    <div :class="$style.voteHeader">
                      <strong>{{ vote.playerUsername }}</strong>
                    </div>
                    <div :class="$style.voteDetails">
                      <div><strong>Voted Text:</strong> {{ vote.votedTextLabel || "N/A" }}</div>
                      <div><strong>Voted Saboteur:</strong> {{ vote.votedSaboteurUsername || "No Saboteur" }}</div>
                      <div v-if="vote.voteReason" :class="$style.voteReason">
                        <strong>Reason:</strong> {{ vote.voteReason }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="gameDetails.scores && Object.keys(gameDetails.scores).length > 0" :class="$style.section">
                <h4 :class="$style.sectionTitle">Final Scores by Group</h4>
                <div v-for="(groupPlayers, groupName) in gameDetails.scores" :key="groupName" :class="$style.groupScores">
                  <h5 :class="$style.groupTitle">{{ groupName }}</h5>
                  <table :class="$style.table">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Role</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="player in groupPlayers" :key="player.id">
                        <td>{{ player.username }}</td>
                        <td>
                          <span :class="[$style.roleBadge, $style[player.role]]">
                            {{ player.role }}
                          </span>
                        </td>
                        <td>{{ player.score }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div v-if="activeTab === 'messages'" :class="$style.tabPanel">
              <div :class="$style.section">
                <h4 :class="$style.sectionTitle">Messages Summary</h4>
                <div :class="$style.detailsGrid">
                  <div :class="$style.detailItem">
                    <span :class="$style.detailLabel">Total Messages:</span>
                    <span :class="$style.detailValue">{{ gameDetails.messagesCount || 0 }}</span>
                  </div>
                  <div v-if="messageGroupOptions.length > 1" :class="$style.detailItem">
                    <span :class="$style.detailLabel">Switch Group:</span>
                    <select v-model="selectedMessageGroup" :class="$style.groupSelect">
                      <option
                        v-for="option in messageGroupOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div v-if="filteredSampleMessages.length > 0" :class="$style.section">
                <h4 :class="$style.sectionTitle">Recent Messages (Sample)</h4>
                <div :class="$style.messagesList">
                  <div v-for="(msg, index) in filteredSampleMessages" :key="index" :class="$style.messageItem">
                    <div :class="$style.messageHeader">
                      <strong>{{ msg.userName }}</strong>
                      <span :class="$style.messageMeta">
                        <span v-if="msg.phase">{{ msg.phase }}</span>
                        <span v-if="msg.groupName" :class="$style.groupTag">{{ msg.groupName }}</span>
                      </span>
                    </div>
                    <div :class="$style.messageText">{{ msg.text }}</div>
                    <div v-if="msg.createdAt" :class="$style.messageTime">{{ formatTime(msg.createdAt) }}</div>
                  </div>
                </div>
                <p v-if="gameDetails.messagesCount > filteredSampleMessages.length" :class="$style.moreMessagesNote">
                  Showing {{ filteredSampleMessages.length }} of {{ gameDetails.messagesCount }} messages. Download full transcript for complete messages.
                </p>
              </div>

              <div :class="$style.section">
                <h4 :class="$style.sectionTitle">Downloads</h4>
                <div :class="$style.actionsGrid">
                  <button
                    :class="$style.downloadBtn"
                    @click="downloadTranscript"
                    :disabled="downloadingTranscript"
                  >
                    {{ downloadingTranscript ? "Downloading..." : "Download Transcript" }}
                  </button>
                  <button
                    :class="$style.downloadBtn"
                    @click="downloadChat"
                    :disabled="downloadingChat"
                  >
                    {{ downloadingChat ? "Downloading..." : "Download Chat" }}
                  </button>
                </div>
                <p :class="$style.downloadNote">
                  Download complete game transcripts and chat messages in Excel format.
                </p>
              </div>
            </div>

            <div v-if="activeTab === 'report'" :class="$style.tabPanel">
              <template v-if="!gameDetails">
                <div :class="$style.loading">Loading game details...</div>
              </template>
              <template v-else>
                <div :class="$style.section">
                  <h4 :class="$style.sectionTitle">Game Report</h4>
                  <p :class="$style.reportNote">
                    Generate a comprehensive analysis report. Reports can only be generated for completed games (DOWNLOAD or END phase).
                    <span v-if="!gameDetails.hasPhaseTranscripts" :class="$style.warningNote">
                      Note: This game does not have detailed phase transcript data. The report will be generated with available information.
                    </span>
                  </p>

                  <div v-if="reportLoading" :class="$style.loading">
                    <div :class="$style.loadingTitle">Generating Report...</div>
                    <div :class="$style.loadingMessage">This may take 30-60 seconds. Please wait while the system analyzes the game data.</div>
                  </div>
                  <div v-else-if="reportError" :class="$style.errorSection">
                    <div :class="$style.errorTitle">Cannot Generate Report</div>
                    <div :class="$style.errorMessage">{{ reportError }}</div>
                    <button :class="$style.generateBtn" @click="generateReport" :disabled="reportLoading">
                      Try Again
                    </button>
                  </div>
                  <div v-else-if="gameReport" :class="$style.reportContent">
                    <div :class="$style.reportText" v-html="formatReport(gameReport)"></div>
                    <button :class="$style.regenerateBtn" @click="generateReport" :disabled="reportLoading">
                      Regenerate Report
                    </button>
                  </div>
                  <div v-else :class="$style.readySection">
                    <p :class="$style.readyMessage">Ready to generate report for this game.</p>
                    <button :class="$style.generateBtn" @click="generateReport" :disabled="reportLoading">
                      Generate Report
                    </button>
                  </div>
                </div>
              </template>
            </div>

            <div v-if="activeTab === 'share'" :class="$style.tabPanel">
              <div :class="$style.shareModeTabs">
                <button
                  :class="[$style.shareModeTab, shareMode === 'public' && $style.shareModeTabActive]"
                  @click="shareMode = 'public'"
                >
                  Public
                </button>
                <button
                  :class="[$style.shareModeTab, shareMode === 'group' && $style.shareModeTabActive]"
                  @click="shareMode = 'group'"
                >
                  Group
                </button>
                <button
                  :class="[$style.shareModeTab, shareMode === 'personal' && $style.shareModeTabActive]"
                  @click="shareMode = 'personal'"
                >
                  Personal
                </button>
              </div>

              <div v-if="shareMode === 'group'" :class="$style.section">
                <h4 :class="$style.sectionTitle">Share to Group</h4>
                <p :class="$style.shareHint">Select a group and classroom context to share this game with other collaborators.</p>
                <div :class="$style.shareGrid">
                  <div :class="$style.shareField">
                    <label :class="$style.detailLabel">Target Group</label>
                    <select v-model="shareGroupForm.groupId" :class="$style.shareInput">
                      <option value="">Select a group</option>
                      <option v-for="group in sharingGroups" :key="group.id" :value="String(group.id)">
                        {{ group.name }}
                      </option>
                    </select>
                  </div>
                  <div :class="$style.shareField">
                    <label :class="$style.detailLabel">Grade Level</label>
                    <select v-model="shareGroupForm.gradeLevel" :class="$style.shareInput">
                      <option value="">Select a grade level</option>
                      <option
                        v-for="opt in shareGradeLevelOptions"
                        :key="opt.selectionValue"
                        :value="opt.selectionValue"
                      >
                        {{ opt.name }}
                      </option>
                    </select>
                  </div>
                  <div :class="$style.shareField">
                    <label :class="$style.detailLabel">Subject / Discipline</label>
                    <select v-model="shareGroupForm.subjectDiscipline" :class="$style.shareInput">
                      <option value="">Select a subject / discipline</option>
                      <option
                        v-for="opt in disciplineOptions"
                        :key="opt.selectionValue"
                        :value="opt.selectionValue"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div :class="$style.shareFieldFull">
                    <label :class="$style.detailLabel">Learning Standard (optional)</label>
                    <textarea
                      v-model="shareGroupForm.learningStandard"
                      rows="3"
                      placeholder="Add a standard code or description, if appropriate"
                      :class="$style.shareInput"
                    />
                  </div>
                </div>
                <p v-if="shareGroupError" :class="$style.shareError">{{ shareGroupError }}</p>
                <div :class="$style.actionsGrid">
                  <button :class="$style.downloadBtn" :disabled="shareGroupLoading || sharingGroupsLoading" @click="shareToGroup">
                    {{ shareGroupLoading ? "Sharing..." : "Share to Group" }}
                  </button>
                </div>
              </div>

              <div v-if="shareMode === 'public'" :class="$style.section">
                <h4 :class="$style.sectionTitle">Share Publicly</h4>
                <p :class="$style.shareHint">Publish this game to the public teacher library with the intended classroom context.</p>
                <div :class="$style.shareGrid">
                  <div :class="$style.shareField">
                    <label :class="$style.detailLabel">Grade Level</label>
                    <select v-model="sharePublicForm.gradeLevel" :class="$style.shareInput">
                      <option value="">Select a grade level</option>
                      <option
                        v-for="opt in shareGradeLevelOptions"
                        :key="opt.selectionValue"
                        :value="opt.selectionValue"
                      >
                        {{ opt.name }}
                      </option>
                    </select>
                  </div>
                  <div :class="$style.shareField">
                    <label :class="$style.detailLabel">Subject / Discipline</label>
                    <select v-model="sharePublicForm.subjectDiscipline" :class="$style.shareInput">
                      <option value="">Select a subject / discipline</option>
                      <option
                        v-for="opt in disciplineOptions"
                        :key="opt.selectionValue"
                        :value="opt.selectionValue"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div :class="$style.shareFieldFull">
                    <label :class="$style.detailLabel">Learning Standard (optional)</label>
                    <textarea
                      v-model="sharePublicForm.learningStandard"
                      rows="3"
                      placeholder="Add a standard code or description, if appropriate"
                      :class="$style.shareInput"
                    />
                  </div>
                </div>
                <p v-if="sharePublicError" :class="$style.shareError">{{ sharePublicError }}</p>
                <div :class="$style.actionsGrid">
                  <button
                    :class="$style.downloadBtn"
                    :disabled="sharePublicLoading || gameDetails?.isPublic === 1"
                    @click="sharePublicly"
                  >
                    {{ gameDetails?.isPublic === 1 ? "Already Public" : (sharePublicLoading ? "Sharing..." : "Share Publicly") }}
                  </button>
                </div>
              </div>

              <div v-if="shareMode === 'personal'" :class="$style.section">
                <h4 :class="$style.sectionTitle">Share to Person</h4>
                <p :class="$style.shareHint">Send this game directly to one collaborator by email.</p>
                <div :class="$style.shareGrid">
                  <div :class="$style.shareFieldFull">
                    <label :class="$style.detailLabel">Recipient Email</label>
                    <input
                      v-model="sharePersonalForm.email"
                      type="email"
                      placeholder="colleague@example.com"
                      :class="$style.shareInput"
                    />
                  </div>
                </div>
                <p v-if="sharePersonalError" :class="$style.shareError">{{ sharePersonalError }}</p>
                <div :class="$style.actionsGrid">
                  <button :class="$style.downloadBtn" :disabled="sharePersonalLoading" @click="shareToPerson">
                    {{ sharePersonalLoading ? "Sharing..." : "Share to Person" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div :class="$style.modalFooter">
          <button :class="$style.closeBtn" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import styles from "@/css/SessionDetailModal.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import gameService from "@/services/game.service";
import { openReplayWindow } from "@/utils/replay";
import { toast } from "vue3-toastify";
import { disciplineOptions, sciArgTargetAudienceOptions } from "@/utils/data";
import { APP_ROUTE_NAMES, USER_ROLES } from "@/utils/permissions";
import { formatSessionTimestamp } from "@/utils/sessionDateTime";
import {
  getConfigFields,
  getOverviewFields,
  getSessionDetailCapabilities,
  getSessionDetailTabs,
  SESSION_VIEW_PRESETS,
  getTextSummaryFields,
} from "@/utils/sessionViews";

export default {
  name: "SessionDetailModal",
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    session: {
      type: Object,
      default: null,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    viewerRole: {
      type: String,
      default: USER_ROLES.ADMIN,
    },
    accessScope: {
      type: String,
      default: SESSION_VIEW_PRESETS.ADMIN_ALL,
    },
    timeZone: {
      type: String,
      default: "UTC",
    },
  },
  data() {
    return {
      closeIcon: CloseIcon,
      disciplineOptions,
      shareGradeLevelOptions: sciArgTargetAudienceOptions,
      gameDetails: null,
      loading: false,
      error: null,
      downloadingTranscript: false,
      downloadingChat: false,
      activeTab: "overview",
      shareMode: "public",
      selectedMessageGroup: "all",
      sharingGroups: [],
      sharingGroupsLoading: false,
      shareGroupForm: {
        groupId: "",
        gradeLevel: "",
        subjectDiscipline: "",
        learningStandard: "",
      },
      sharePublicForm: {
        gradeLevel: "",
        subjectDiscipline: "",
        learningStandard: "",
      },
      sharePersonalForm: {
        email: "",
      },
      shareGroupLoading: false,
      sharePublicLoading: false,
      sharePersonalLoading: false,
      shareGroupError: "",
      sharePublicError: "",
      sharePersonalError: "",
      gameReport: null,
      reportLoading: false,
      reportError: null,
    };
  },
  computed: {
    $style() {
      return styles;
    },
    sessionCapabilities() {
      return getSessionDetailCapabilities({
        gameDetails: this.gameDetails,
        accessScope: this.accessScope,
      });
    },
    visibleTabs() {
      return getSessionDetailTabs({
        gameDetails: this.gameDetails,
        accessScope: this.accessScope,
      }).filter((tab) => tab.visible !== false);
    },
    overviewFields() {
      return getOverviewFields(this.gameDetails, { viewerRole: this.viewerRole, accessScope: this.accessScope });
    },
    configFields() {
      return getConfigFields(this.gameDetails, { viewerRole: this.viewerRole, accessScope: this.accessScope });
    },
    textSummaryFields() {
      return getTextSummaryFields(this.gameDetails);
    },
    playersWithoutHost() {
      if (!this.gameDetails || !this.gameDetails.players) return [];
      const hostId = this.gameDetails.hostId != null ? String(this.gameDetails.hostId) : null;
      return this.gameDetails.players.filter(
        (p) => p.role !== "host" && (hostId == null || String(p.id) !== hostId)
      );
    },
    messageGroupOptions() {
      const players = this.gameDetails?.players || [];
      const groups = Array.from(
        new Set(players.map((player) => player.groupName).filter(Boolean))
      );
      const sortedGroups = groups.sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
      );
      return [
        { value: "all", label: "All Groups" },
        ...sortedGroups.map((group) => ({ value: group, label: group })),
      ];
    },
    filteredSampleMessages() {
      const messages = this.gameDetails?.sampleMessages || [];
      if (this.selectedMessageGroup === "all") return messages;
      return messages.filter((msg) => msg.groupName === this.selectedMessageGroup);
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal && this.session) {
        this.loadGameDetails();
      } else {
        this.gameDetails = null;
        this.error = null;
        this.loading = false;
        this.activeTab = "overview";
        this.shareMode = "public";
        this.selectedMessageGroup = "all";
        this.sharingGroups = [];
        this.shareGroupError = "";
        this.sharePublicError = "";
        this.sharePersonalError = "";
        this.gameReport = null;
        this.reportError = null;
      }
    },
    session: {
      handler(newVal) {
        if (newVal && this.showModal) {
          this.loadGameDetails();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.showModal && this.session) {
      this.loadGameDetails();
    }
  },
  methods: {
    openReplay() {
      const gameId = this.gameDetails?.gameId;
      if (!gameId) return;
      const { href } = this.$router.resolve({ name: APP_ROUTE_NAMES.GAME_REPLAY, params: { gameId } });
      if (!openReplayWindow(href, gameId)) {
        toast(this.$t("replay.popupBlocked"), { type: "error" });
      }
    },
    textLabel(index) {
      return index < 26 ? `Text ${String.fromCharCode(65 + index)}` : `Text ${index + 1}`;
    },
    disciplineLabel(selectionValue) {
      if (!selectionValue || selectionValue === "N/A") return "N/A";
      const opt = disciplineOptions.find((o) => o.selectionValue === selectionValue);
      return opt ? opt.label : selectionValue;
    },
    formatOverviewValue(field) {
      if (field.key === "discipline") return this.disciplineLabel(field.value);
      if (field.key === "startTime" || field.key === "endTime") {
        return field.value && field.value !== "N/A"
          ? formatSessionTimestamp(field.value, this.timeZone)
          : field.value;
      }
      return field.value;
    },
    formatConfigValue(field) {
      if (field.key === "discipline") return this.disciplineLabel(field.value);
      return field.value;
    },
    async loadGameDetails() {
      if (!this.session || !this.session.gameId) {
        this.error = "Invalid game data";
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;
      this.gameDetails = null;
      this.selectedMessageGroup = "all";

      try {
        const res = await gameService.getGameDetails(this.session.gameId);

        if (res && res.status === 200 && res.data) {
          if (res.data.success) {
            this.gameDetails = {
              ...res.data,
              shareGradeLevel: this.session?.shareGradeLevel ?? res.data.shareGradeLevel ?? null,
              shareSubjectDiscipline: this.session?.shareSubjectDiscipline ?? res.data.shareSubjectDiscipline ?? null,
              shareLearningStandard: this.session?.shareLearningStandard ?? res.data.shareLearningStandard ?? null,
            };
            this.prefillShareForms();
            if (this.gameDetails.canShareGame) {
              await this.loadSharingGroups();
            }
          } else {
            this.error = res.data.message || "Failed to load game details";
            toast.error(this.error);
          }
        } else {
          this.error = res?.data?.message || "Failed to load game details";
          toast.error(this.error);
        }
      } catch (err) {
        this.error = err.message || "Failed to load game details";
        toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    prefillShareForms() {
      const gradeLevel = this.gameDetails?.shareGradeLevel || "";
      const subjectDiscipline = this.gameDetails?.shareSubjectDiscipline || this.gameDetails?.discipline || "";
      const learningStandard = this.gameDetails?.shareLearningStandard || "";
      this.shareGroupForm = {
        groupId: "",
        gradeLevel,
        subjectDiscipline,
        learningStandard,
      };
      this.sharePublicForm = {
        gradeLevel,
        subjectDiscipline,
        learningStandard,
      };
      this.sharePersonalForm = {
        email: "",
      };
      this.shareGroupError = "";
      this.sharePublicError = "";
      this.sharePersonalError = "";
    },
    async loadSharingGroups() {
      this.sharingGroupsLoading = true;
      try {
        const res = await gameService.getMySharingGroups(this.viewerRole);
        if (res?.success && Array.isArray(res.groups)) {
          this.sharingGroups = res.groups;
        } else {
          this.sharingGroups = [];
        }
      } catch (err) {
        this.sharingGroups = [];
      } finally {
        this.sharingGroupsLoading = false;
      }
    },
    async shareToGroup() {
      if (!this.session?.gameId) return;
      const groupId = (this.shareGroupForm.groupId || "").trim();
      const gradeLevel = (this.shareGroupForm.gradeLevel || "").trim();
      const subjectDiscipline = (this.shareGroupForm.subjectDiscipline || "").trim();
      const learningStandard = (this.shareGroupForm.learningStandard || "").trim();
      if (!groupId || !gradeLevel || !subjectDiscipline) {
        this.shareGroupError = "Group, grade level, and subject / discipline are required.";
        return;
      }
      this.shareGroupLoading = true;
      this.shareGroupError = "";
      try {
        const res = await gameService.shareGameToGroup(
          Number(groupId),
          this.session.gameId,
          { gradeLevel, subjectDiscipline, learningStandard },
          this.viewerRole
        );
        toast.success(res?.message || "Game shared.");
      } catch (err) {
        const msg = err?.message || err?.error || "Failed to share to group.";
        this.shareGroupError = msg;
        toast.error(msg);
      } finally {
        this.shareGroupLoading = false;
      }
    },
    async sharePublicly() {
      if (!this.session?.gameId || this.gameDetails?.isPublic === 1) return;
      const gradeLevel = (this.sharePublicForm.gradeLevel || "").trim();
      const subjectDiscipline = (this.sharePublicForm.subjectDiscipline || "").trim();
      const learningStandard = (this.sharePublicForm.learningStandard || "").trim();
      if (!gradeLevel || !subjectDiscipline) {
        this.sharePublicError = "Grade level and subject / discipline are required.";
        return;
      }
      this.sharePublicLoading = true;
      this.sharePublicError = "";
      try {
        const res = await gameService.switchGameVisibility(this.session.gameId, {
          gradeLevel,
          subjectDiscipline,
          learningStandard,
        });
        if (res?.status === 200 && res?.data?.success) {
          this.gameDetails.isPublic = 1;
          this.gameDetails.shareGradeLevel = res.data.shareGradeLevel ?? gradeLevel;
          this.gameDetails.shareSubjectDiscipline = res.data.shareSubjectDiscipline ?? subjectDiscipline;
          this.gameDetails.shareLearningStandard = res.data.shareLearningStandard ?? learningStandard;
          toast.success("Game shared publicly.");
        } else {
          const msg = res?.data?.message || res?.data?.error || "Failed to share publicly.";
          this.sharePublicError = msg;
          toast.error(msg);
        }
      } catch (err) {
        const msg = err?.message || err?.error || "Failed to share publicly.";
        this.sharePublicError = msg;
        toast.error(msg);
      } finally {
        this.sharePublicLoading = false;
      }
    },
    async shareToPerson() {
      if (!this.session?.gameId) return;
      const email = (this.sharePersonalForm.email || "").trim();
      if (!email) {
        this.sharePersonalError = "Recipient email is required.";
        return;
      }
      this.sharePersonalLoading = true;
      this.sharePersonalError = "";
      try {
        await gameService.shareGamePersonal(this.session.gameId, email);
        toast.success(`Game shared with ${email}`);
        this.sharePersonalForm.email = "";
      } catch (err) {
        const msg = err?.message || err?.error || "Failed to share to person.";
        this.sharePersonalError = msg;
        toast.error(msg);
      } finally {
        this.sharePersonalLoading = false;
      }
    },
    async downloadTranscript() {
      if (!this.session || !this.session.gameId) {
        toast.error("Invalid game ID");
        return;
      }

      this.downloadingTranscript = true;
      try {
        await gameService.downloadTranscript(this.session.gameId, this.timeZone);
        toast.success("Transcript download started");
      } catch (err) {
        toast.error(err.message || "Failed to download transcript");
      } finally {
        this.downloadingTranscript = false;
      }
    },
    async downloadChat() {
      if (!this.session || !this.session.gameId) {
        toast.error("Invalid game ID");
        return;
      }

      this.downloadingChat = true;
      try {
        await gameService.downloadChat(this.session.gameId, this.timeZone);
        toast.success("Chat download started");
      } catch (err) {
        toast.error(err.message || "Failed to download chat");
      } finally {
        this.downloadingChat = false;
      }
    },
    formatTime(timeStr) {
      return formatSessionTimestamp(timeStr, this.timeZone);
    },
    async generateReport() {
      if (!this.session || !this.session.gameId) {
        toast.error("Invalid game ID");
        return;
      }

      this.reportLoading = true;
      this.reportError = null;
      this.gameReport = null;

      try {
        const res = await gameService.generateGameReport(this.session.gameId);
        if (res.status === 200 && res.data) {
          if (res.data.success && res.data.report) {
            this.gameReport = res.data.report;
            toast.success("Report generated successfully");
          } else if (!res.data.isGameComplete) {
            this.reportError = res.data.error || "Game is not yet complete. Reports can only be generated after the game reaches DOWNLOAD or END phase.";
            toast.warning("Game not complete");
          } else {
            this.reportError = res.data.error || "Failed to generate report";
            toast.error(this.reportError);
          }
        } else {
          this.reportError = res?.data?.error || res?.data?.message || "Failed to generate report";
          toast.error(this.reportError);
        }
      } catch (err) {
        this.reportError = err.message || "Failed to generate report";
        toast.error(this.reportError);
      } finally {
        this.reportLoading = false;
      }
    },
    formatReport(reportText) {
        if (!reportText) return "";
        let formatted = String(reportText);
        formatted = formatted.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const sectionClass = this.$style.reportSection || "";
        const subClass = this.$style.reportSubsection || "";
        const pClass = this.$style.reportParagraph || "";
        const h3Class = this.$style.reportH3 || "";
        const h4Class = this.$style.reportH4 || "";
        formatted = formatted.replace(/^### (.+)$/gm, `<h3 class="${h3Class}">$1</h3>`);
        formatted = formatted.replace(/^#### (.+)$/gm, `<h4 class="${h4Class}">$1</h4>`);
        formatted = formatted.replace(/^(EXECUTIVE SUMMARY|PHASE ANALYSIS|CRITICAL THINKING|GROUP DYNAMICS|SABOTEUR DETECTION|PLAYER INSIGHTS|RECOMMENDATIONS)(\s*)$/gm, `<h4 class="${sectionClass}">$1</h4>`);
        formatted = formatted.replace(/^(\d+\.\s+[A-Z][^:\n]*:?)/gm, `<h5 class="${subClass}">$1</h5>`);
        formatted = formatted.replace(/\*\*\*([^*]*?)\*\*\*/g, "<strong><em>$1</em></strong>");
        formatted = formatted.replace(/\*\*([^*]*?)\*\*/g, "<strong>$1</strong>");
        formatted = formatted.replace(/\*([^*]*?)\*/g, "<em>$1</em>");
        formatted = formatted.replace(/^\s*-\s+(.+)$/gm, "<li>$1</li>");
        formatted = formatted.replace(/(<li>.*?<\/li>\n?)+/gs, "<ul class=\"" + (this.$style.reportList || "") + "\">$1</ul>");
        formatted = formatted.replace(/\n\n+/g, "\n\n");
        const blocks = formatted.split(/\n\n/);
        const wrapped = blocks.map((block) => {
          const trimmed = block.trim();
          if (!trimmed) return "";
          if (/^<(h[34]|ul|ol)/.test(trimmed)) return trimmed;
          return `<p class="${pClass}">${trimmed.replace(/\n/g, "<br>")}</p>`;
        }).filter(Boolean).join("\n");
        return `<div class="${this.$style.reportInner || ""}">${wrapped}</div>`;
    },
  },
};
</script>

<style module>
</style>
